/** Segmented 6-digit code entry. Owns six single-char inputs and their focus;
 * the pure decisions (advance/backspace/paste/complete) live in
 * lib/codeInput.logic so they're unit-tested without a DOM. */

import {
  backspace,
  code,
  CODE_LENGTH,
  fillFrom,
  isComplete,
} from "../lib/codeInput.logic";
import { h } from "../lib/jsx";

export interface CodeInput {
  nodes: Element[];
  value(): string;
  /** Empty the cells. Notifies onChange, like any other value change. */
  clear(): void;
  focusFirst(): void;
}

export interface CodeInputOptions {
  /** The value changed, however it changed — typing, backspace, paste or
   * clear(). The six cells and their events are this component's business, so
   * callers gate their CTA on this rather than reconstructing "the code
   * changed" from DOM events on the group. */
  onChange?: (code: string) => void;
  /** Edge-triggered on the sixth digit: the code is now complete. Fires after
   * onChange for the same keystroke. */
  onComplete?: (code: string) => void;
}

export function codeInput(opts: CodeInputOptions = {}): CodeInput {
  const cells: HTMLInputElement[] = [];
  let state = Array<string>(CODE_LENGTH).fill("");
  let complete = false;

  function sync(focus: number): void {
    cells.forEach((cell, i) => {
      cell.value = state[i] ?? "";
    });
    cells[focus]?.focus();
    cells[focus]?.select();
    const nowComplete = isComplete(state);
    opts.onChange?.(code(state));
    if (nowComplete && !complete) opts.onComplete?.(code(state));
    complete = nowComplete;
  }

  for (let i = 0; i < CODE_LENGTH; i += 1) {
    const index = i;
    // No maxlength, and every cell offers the autofill: iOS clips a
    // one-time-code fill to the field's length, and fills the focused cell.
    const cell = (
      <input
        class="otp-code-cell"
        type="text"
        inputmode="numeric"
        aria-label={`Digit ${index + 1} of ${CODE_LENGTH}`}
        autocomplete="one-time-code"
        onFocus={(e: Event) => (e.target as HTMLInputElement).select()}
        onInput={(e: Event) => {
          const r = fillFrom(state, index, inserted(e as InputEvent));
          state = r.cells;
          sync(r.focus);
        }}
        onKeyDown={(e: KeyboardEvent) => {
          if (e.key === "Backspace") {
            e.preventDefault();
            const r = backspace(state, index);
            state = r.cells;
            sync(r.focus);
          }
        }}
        onPaste={(e: ClipboardEvent) => {
          e.preventDefault();
          const raw = e.clipboardData?.getData("text") ?? "";
          const r = fillFrom(state, index, raw);
          state = r.cells;
          sync(r.focus);
        }}
      />
    ) as HTMLInputElement;
    cells.push(cell);
  }

  return {
    nodes: [
      <label class="otp-label otp-visually-hidden" id="otp-code-label">6 digit code</label>,
      <div class="otp-code-group" role="group" aria-labelledby="otp-code-label">
        {cells}
      </div>,
    ],
    value: () => code(state),
    clear() {
      state = Array<string>(CODE_LENGTH).fill("");
      complete = false;
      cells.forEach((c) => (c.value = ""));
      opts.onChange?.(code(state));
    },
    focusFirst() {
      cells[0]?.focus();
    },
  };
}

/** What to write: one inserted character replaces the cell, anything else (a
 * paste, an autofilled code) is the whole field. Without maxlength a keystroke
 * on a filled cell would otherwise read as "old digit + new" and spill over. */
function inserted(e: InputEvent): string {
  return e.data?.length === 1 ? e.data : (e.target as HTMLInputElement).value;
}
